// Modules
import React, { useState, useEffect, useCallback } from 'react';

// Hooks
import useInput from 'utils/hooks/useInput';

// Api
import { getSetupIds } from 'utils/api/payfabricApi';

// Utils
import generateRandomID from 'utils/helpers/generateRandomID';

// Components
import {
  Modal,
  Form,
  Button,
  OverlayTrigger,
  Popover,
  Collapse,
} from 'react-bootstrap';
import Loading from 'components/Common/Loading';

// Constants
import PaymentPlatforms from 'constants/PaymentPlatforms';
import FetchStatuses from 'constants/FetchStatuses';

import styles from './ModalFormElements.module.scss';
import DevicesList from 'components/Common/DevicesList/DevicesList';
import GlobalPayDevicesList from 'components/Common/DevicesList/GlobalPayDevicesList';
import { DeviceTypes } from 'constants/Payfabric';

function ModalFormElements(props) {
  const {
    addPayFabricDeviceId,
    connectTo,
    currentPayFabricDeviceIds,
    deletePayfabricDeviceId,
    addGlobalPayDeviceId,
    currentGlobalPayDeviceIds,
    deleteGlobalPayDeviceId,
    payProcessorUpdateData,
    resetGlobalPayDeviceIds,
    resetPayFabricDeviceIds,
    setPayProcessorUpdateData,
    setShowModal,
    showModal,
  } = props;

  const [updating, setUpdating] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});

  const [payFabricDeviceId, setPayFabricDeviceId] = useInput();
  const [payFabricDevicePassword, setPayFabricDevicePassword] = useInput();
  const [payFabricSetupId, setPayFabricSetupId] = useInput();
  const [payFabricDeviceType, setPayFabricDeviceType] = useInput();
  const [globalPayDeviceId, setGlobalPayDeviceId] = useInput();
  const [globalPayDeviceName, setGlobalPayDeviceName] = useInput();
  const [fetchedSetupIds, setFetchedSetupIds] = useState([]);
  const [setupIdStatus, setSetupIdStatus] = useState(FetchStatuses.idle);
  const [openDeviceForm, setOpenDeviceForm] = useState(false);
  const [deviceAdded, setDeviceAdded] = useState(false);
  const [deviceTypeOptions, setDeviceTypeOptions] = useState([
    { title: 'Select', key: '--' },
    { title: 'Card Not Present', key: 'CardNotPresent' },
    { title: 'Saved Card', key: 'SavedCard' },
    { title: 'Terminal', key: 'Terminal' },
  ]);

  const [globalPayMerchantCredentials, setGlobalPayMerchantCredentials] =
    useInput(
      payProcessorUpdateData?.globalPayMerchantCredentials ??
        props?.globalPayMerchantCredentials ??
        ''
    );

  function resetPayFabricFields() {
    setFetchedSetupIds([]);
    setPayFabricSetupId('');
    setPayFabricDeviceType('');
    setSetupIdStatus(FetchStatuses.idle);
  }

  const fillPayFabricDeviceTypeSelectValues = useCallback(() => {
    setDeviceTypeOptions(
      [
        { title: 'Select', key: '--' },
        !currentPayFabricDeviceIds?.defaultDeviceId && {
          title: 'Card Not Present',
          key: 'CardNotPresent',
        },
        !currentPayFabricDeviceIds?.savedCardDeviceId && {
          title: 'Saved Card',
          key: 'SavedCard',
        },
        !currentPayFabricDeviceIds?.terminalDeviceId && {
          title: 'Terminal',
          key: 'Terminal',
        },
      ].filter(Boolean)
    );
  }, [setDeviceTypeOptions, currentPayFabricDeviceIds]);

  useEffect(() => {
    fillPayFabricDeviceTypeSelectValues();
  }, [fillPayFabricDeviceTypeSelectValues]);

  async function handleFetchSetupIds() {
    if (setupIdStatus === FetchStatuses.pending) return;

    setErrorMessages({});
    resetPayFabricFields();

    if (!payFabricDeviceId || !payFabricDevicePassword) {
      setErrorMessages({
        payFabricErr: 'Please Enter Device ID and Password',
      });
      return;
    }

    try {
      setSetupIdStatus(FetchStatuses.pending);

      const data = await getSetupIds(
        payFabricDeviceId,
        payFabricDevicePassword
      );

      const setupIds = data.map((gateway) => gateway.Name);

      if (setupIds.length === 0) {
        setSetupIdStatus(FetchStatuses.rejected);
        setErrorMessages({
          payFabricErr:
            'No SetupIDs were found for this device, please contact PayFabric for help.',
        });
        return;
      }

      setFetchedSetupIds(setupIds);
      setPayFabricSetupId(setupIds[0]);
      setSetupIdStatus(FetchStatuses.resolved);
    } catch (err) {
      setSetupIdStatus(FetchStatuses.rejected);
      setErrorMessages({
        payFabricErr:
          'Could not validate Device ID and Password, please verify',
      });
    }
  }

  function getSelectDisplayValue() {
    switch (setupIdStatus) {
      case FetchStatuses.idle:
      case FetchStatuses.rejected:
        return 'Select a SetupID for practice';
      case FetchStatuses.pending:
        return 'Loading...';
      case FetchStatuses.resolved:
        return payFabricSetupId;
      default:
        throw new Error(`Unrecognizable status: ${setupIdStatus}`);
    }
  }

  const isDisabled = () => {
    if (connectTo === PaymentPlatforms.payfabric) {
      return (
        !payFabricDeviceId ||
        !payFabricDevicePassword ||
        !payFabricSetupId ||
        payFabricDeviceType === '--' ||
        payFabricDeviceType === ''
      );
    }
  };

  const handleExit = (closeModal = false, isCancel = true) => {
    // reset fields upon modal exit to be sure that bad data doesn't get sent to backend
    resetPayFabricFields();
    setPayFabricDeviceId('');
    setPayFabricDevicePassword('');
    setErrorMessages({});
    resetPayFabricDeviceIds();
    resetGlobalPayDeviceIds();

    if (closeModal) {
      setShowModal(false);
    } else {
      setOpenDeviceForm(false);
    }
  };

  const getPayFabricDetails = (deviceConfig, prev) => {
    if (deviceConfig) {
      return prev?.payFabricDevices?.length >= 0
        ? [
            ...prev.payFabricDevices,
            {
              [deviceConfig.id]: payFabricDeviceId,
              [deviceConfig.password]: payFabricDevicePassword,
              [deviceConfig.setupId]: payFabricSetupId,
              deviceType: payFabricDeviceType,
            },
          ]
        : [
            {
              [deviceConfig.id]: payFabricDeviceId,
              [deviceConfig.password]: payFabricDevicePassword,
              [deviceConfig.setupId]: payFabricSetupId,
              deviceType: payFabricDeviceType,
            },
          ];
    } else {
      return [];
    }
  };

  const updateSuccess = (closeModal = false) => {
    setPayProcessorUpdateData((prev) => {
      const deviceConfig =
        DeviceTypes[payFabricDeviceType || prev?.payFabricDeviceType];

      return {
        ...prev,
        payFabricDevices: [...getPayFabricDetails(deviceConfig, prev)],
        globalPayDeviceIds: currentGlobalPayDeviceIds,
        globalPayMerchantCredentials: globalPayMerchantCredentials,
      };
    });

    handleExit(closeModal, false);
  };

  function handlePayFabricConnect() {
    // we can just continue as validation has already been done
    updateSuccess(false);
    setUpdating(false);
    addPayFabricDeviceId(payFabricDeviceId, payFabricDeviceType);
    setDeviceAdded(true);
  }

  const handleConnect = () => {
    setUpdating(true);

    if (connectTo === PaymentPlatforms.payfabric) {
      handlePayFabricConnect();
    }
  };

  const handleGlobalPayAdd = () => {
    if (!globalPayDeviceId || !globalPayDeviceName) {
      setErrorMessages({
        globalPayErr: 'Please Enter Device ID and Name',
      });
      return;
    }

    updateSuccess(false);
    setUpdating(false);
    addGlobalPayDeviceId(globalPayDeviceId, globalPayDeviceName);
    setDeviceAdded(true);
  };

  const getOpenDeviceFormIcon = () => {
    return openDeviceForm ? 'fa-caret-down' : 'fa-plus';
  };

  const getSetupIdStatus = () => {
    return setupIdStatus === FetchStatuses.pending ? styles.spin : '';
  };

  const showNewConnectDeviceForm =
    !!currentPayFabricDeviceIds?.defaultDeviceId &&
    !!currentPayFabricDeviceIds?.savedCardDeviceId &&
    !!currentPayFabricDeviceIds?.terminalDeviceId
      ? 'd-none'
      : '';

  const SubmitButton = (_props) =>
    _props.paymentPlatform === PaymentPlatforms.payfabric ? (
      <Button onClick={() => setShowModal(false)}>UPDATE</Button>
    ) : _props.paymentPlatform === PaymentPlatforms.globalpay ? (
      <Button onClick={() => updateSuccess(true)}>UPDATE</Button>
    ) : (
      <Button
        onClick={handleConnect}
        disabled={isDisabled()}
      >{`CONNECT TO ${connectTo.toUpperCase()}`}</Button>
    );

  return (
    <Modal
      show={showModal}
      onHide={() => null}
      size='md'
      aria-labelledby={`update-modal-title-${connectTo}`}
      centered
    >
      <Modal.Header>
        <Modal.Title id={`update-modal-title-${connectTo}`}>
          {`CONNECT TO ${connectTo.toUpperCase()}`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {connectTo === PaymentPlatforms.payfabric ? (
          <>
            <DevicesList
              payFabricDeviceIds={currentPayFabricDeviceIds}
              deletePayfabricDeviceId={deletePayfabricDeviceId}
            />
            <div
              className={`row ${showNewConnectDeviceForm}`}
              type='button'
              onClick={() => setOpenDeviceForm(!openDeviceForm)}
            >
              <p className={`col ${styles.newDeviceFormTitle}`}>
                CONNECT NEW DEVICE
              </p>
              <div type='button' className='mr-3'>
                <i className={`fas ${getOpenDeviceFormIcon()}`}></i>
              </div>
            </div>
            <Collapse in={openDeviceForm}>
              <div className={styles.newDeviceForm}>
                <Form.Group controlId='payFabricDeviceId'>
                  <Form.Label>Device ID</Form.Label>
                  <Form.Control
                    type='text'
                    name='payFabricDeviceId'
                    value={payFabricDeviceId}
                    onChange={(e) => {
                      resetPayFabricFields();
                      setPayFabricDeviceId(e.target.value.trim());
                    }}
                    placeholder='Enter PayFabric Device ID for practice'
                  />
                  <Form.Text className='text-muted'>
                    Enter PayFabric Device ID for practice
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId='payFabricDevicePassword'>
                  <Form.Label>Device Password</Form.Label>
                  <Form.Control
                    type='text'
                    name='payFabricDevicePassword'
                    value={payFabricDevicePassword}
                    onBlur={handleFetchSetupIds}
                    onChange={(e) => {
                      resetPayFabricFields();
                      setPayFabricDevicePassword(e.target.value);
                    }}
                    placeholder='Enter PayFabric Device Password for practice'
                  />
                  <Form.Text className='text-muted'>
                    Enter PayFabric Device Password for practice
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId='payFabricSetupId'>
                  <Form.Label>Setup ID</Form.Label>

                  {/* Refresh Button */}
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement='top'
                    overlay={
                      <Popover className='popover-basic'>
                        <Popover.Content>Find Setup IDs</Popover.Content>
                      </Popover>
                    }
                  >
                    <i
                      className={`fa fa-sync-alt ml-1 ${getSetupIdStatus()} ${
                        styles.refreshButton
                      }`}
                      onClick={handleFetchSetupIds}
                    />
                  </OverlayTrigger>

                  {/* Select */}
                  <div>
                    <select
                      className='form-control'
                      onChange={(e) => setPayFabricSetupId(e.target.value)}
                      value={
                        fetchedSetupIds.length === 0 ? 'none' : payFabricSetupId
                      }
                    >
                      {fetchedSetupIds.length === 0 ? (
                        <option value='none' disabled>
                          {getSelectDisplayValue()}
                        </option>
                      ) : (
                        fetchedSetupIds.map((setupId, i) => (
                          <option key={i} value={setupId}>
                            {setupId}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <Form.Text className='text-muted'>
                    Select refresh to load the latest Setup IDs for practice{' '}
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId='payFabricDeviceType'>
                  <Form.Label>Device Type</Form.Label>
                  {deviceTypeOptions.length > 1 && (
                    <div>
                      <select
                        className='form-control'
                        onChange={(e) => setPayFabricDeviceType(e.target.value)}
                        value={payFabricDeviceType}
                      >
                        {deviceTypeOptions.map((val) => {
                          return (
                            <option key={val.key} value={val.key}>
                              {val.title}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  )}
                  <Form.Text className='text-muted'>
                    Select the type of this new Device ID
                  </Form.Text>
                </Form.Group>

                <Button onClick={handleConnect} disabled={isDisabled()}>
                  CONNECT TO PAYFABRIC
                </Button>
              </div>
            </Collapse>
          </>
        ) : null}

        {connectTo === PaymentPlatforms.globalpay ? (
          <>
            <p className={`col ${styles.newDeviceFormTitle}`}>
              GLOBALPAY MERCHANT CREDENTIALS
            </p>
            <Form.Group controlId='globalPayMerchantCredentials'>
              <Form.Label>Merchant Credentials</Form.Label>
              <Form.Control
                type='text'
                name='globalPayMerchantCredentials'
                value={globalPayMerchantCredentials}
                onChange={(e) => {
                  setGlobalPayMerchantCredentials(e.target.value.trim());
                }}
                placeholder='Enter the Merchant Credentials for your account'
              />
              <Form.Text className='text-muted'>
                Enter the Merchant Credentials for your account
              </Form.Text>
            </Form.Group>
            <GlobalPayDevicesList
              globalPayDeviceIds={currentGlobalPayDeviceIds}
              deleteGlobalPayDeviceId={deleteGlobalPayDeviceId}
            />
            <div
              className={`row ${showNewConnectDeviceForm}`}
              type='button'
              onClick={() => setOpenDeviceForm(!openDeviceForm)}
            >
              <p className={`col ${styles.newDeviceFormTitle}`}>
                ADD NEW DEVICE
              </p>
              <div type='button' className='mr-3'>
                <i className={`fas ${getOpenDeviceFormIcon()}`}></i>
              </div>
            </div>
            <Collapse in={openDeviceForm}>
              <div className={styles.newDeviceForm}>
                <Form.Group controlId='globalPayDeviceId'>
                  <Form.Label>Device ID</Form.Label>
                  <Form.Control
                    type='text'
                    name='globalPayDeviceId'
                    value={globalPayDeviceId}
                    onChange={(e) => {
                      setGlobalPayDeviceId(e.target.value.trim());
                    }}
                    placeholder='Enter the Cloud Device ID for this device'
                  />
                  <Form.Text className='text-muted'>
                    Enter the Cloud Device ID for this device
                  </Form.Text>
                </Form.Group>
                <Form.Group controlId='globalPayDeviceName'>
                  <Form.Label>Device Name</Form.Label>
                  <Form.Control
                    type='text'
                    name='globalPayDeviceName'
                    value={globalPayDeviceName}
                    onChange={(e) => {
                      setGlobalPayDeviceName(e.target.value);
                    }}
                    placeholder='Enter Device Name'
                  />
                  <Form.Text className='text-muted'>
                    Enter the practice name for this device, e.g. Front Desk 1
                  </Form.Text>
                </Form.Group>

                <Button onClick={handleGlobalPayAdd}>
                  ADD GLOBALPAY DEVICE
                </Button>
              </div>
            </Collapse>
          </>
        ) : null}

        {Object.values(errorMessages).map((msg) => (
          <p key={generateRandomID()} className='invalid-feedback d-block'>
            {msg}
          </p>
        ))}
        {deviceAdded &&
          (connectTo === PaymentPlatforms.payfabric ||
            connectTo === PaymentPlatforms.globalpay) && (
            <p className={styles.successFeedback}>
              Successfully added the device, please click Save to finalize your
              changes.
            </p>
          )}
      </Modal.Body>

      <Modal.Footer
        className={`d-flex align-center ${
          updating ? 'justify-content-center' : 'justify-content-between'
        }`}
      >
        {updating ? (
          <Loading
            style={{
              alignItems: 'center',
            }}
            message='Validating...'
          />
        ) : (
          <>
            <Button variant='light' onClick={handleExit}>
              CANCEL
            </Button>
            <SubmitButton paymentPlatform={connectTo} />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ModalFormElements;
